package fr.manyverse.app.presentation.components.common_tabs

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.BiasAlignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.clipPath
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.logo
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource

/**
 * Couleur du feutre intérieur de la piste de dés. Indépendante du thème (cosmétique).
 * Le rebord 3D dérive de la couleur du feutre — seule la teinte change.
 * [id] est l'identifiant stable persisté dans les préférences.
 */
enum class DiceTrayColor(
    val id: String,
    val center: Color,
    val edge: Color,
    val logoRes: DrawableResource? = null
) {
    GREEN("green", Color(0xFF1FA85C), Color(0xFF0B5A30)),
    BLUE("blue", Color(0xFF2F73C0), Color(0xFF123A66)),
    RED("red", Color(0xFFB23440), Color(0xFF551018)),
    PURPLE("purple", Color(0xFF6A45A8), Color(0xFF2A1550)),
    CHARCOAL("charcoal", Color(0xFF454A54), Color(0xFF181B21)),
    MANYVERSE("manyverse", Color(0xFF1A1A1A), Color(0xFF000000), Res.drawable.logo);

    companion object {
        val DEFAULT = MANYVERSE
        fun fromId(id: String?): DiceTrayColor = entries.firstOrNull { it.id == id } ?: DEFAULT
    }
}

// Géométrie du bloc : coins arrondis et épaisseur du rebord 3D.
private val TRAY_CORNER = 28.dp
private val TRAY_RAIL = 7.dp

// Filigrane : largeur relative de l'image et biais vertical de son centre.
private const val LOGO_WIDTH_FRACTION = 0.4f
private const val LOGO_CENTER_BIAS = -2f / 3f

/**
 * Fraction de la hauteur occupée par le filigrane du logo (0 s'il n'y en a pas), marge
 * comprise. Permet aux dés 3D de se ranger sous le logo plutôt que par-dessus. La hauteur
 * du logo est estimée à partir de sa largeur (image sensiblement carrée).
 */
internal fun trayLogoBottomFraction(color: DiceTrayColor, widthPx: Float, heightPx: Float): Float {
    if (color.logoRes == null || heightPx <= 0f) return 0f
    val centerY = heightPx * (1f + LOGO_CENTER_BIAS) / 2f
    val bottom = centerY + widthPx * LOGO_WIDTH_FRACTION / 2f
    return (bottom / heightPx + 0.03f).coerceIn(0f, 0.5f)
}

/**
 * Piste de dés : bloc occupant tout l'espace disponible, avec une bordure 3D arrondie
 * (rebord biseauté clair en haut / sombre en bas) rappelant le rebord d'une piste, et un
 * feutre marbré coloré en retrait. Le [content] (résultat) est centré ; un éventuel logo
 * est affiché en filigrane, centré dans le tiers supérieur.
 */
@Composable
fun DiceTray(
    color: DiceTrayColor,
    modifier: Modifier = Modifier,
    content: @Composable BoxScope.() -> Unit
) {
    Box(
        modifier = modifier,
        contentAlignment = Alignment.Center
    ) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            drawTray(color)
        }
        color.logoRes?.let { res ->
            Box(
                modifier = Modifier.fillMaxSize(),
                // Centre du tiers supérieur ≈ 1/6 de la hauteur → biais vertical -2/3.
                contentAlignment = BiasAlignment(0f, LOGO_CENTER_BIAS)
            ) {
                Image(
                    painter = painterResource(res),
                    contentDescription = null,
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.fillMaxWidth(LOGO_WIDTH_FRACTION)
                )
            }
        }
        content()
    }
}

private fun DrawScope.drawTray(color: DiceTrayColor) {
    val corner = CornerRadius(TRAY_CORNER.toPx())
    val rail = TRAY_RAIL.toPx()

    // Rebord 3D : dégradé vertical biseauté (clair en haut → sombre en bas), dérivé du feutre.
    val railLight = lerp(color.edge, Color.White, 0.25f)
    val railBase = lerp(color.edge, Color.Black, 0.35f)
    val railDark = lerp(color.edge, Color.Black, 0.55f)
    drawRoundRect(
        brush = Brush.verticalGradient(listOf(railLight, railBase, railDark)),
        cornerRadius = corner
    )

    // Feutre intérieur en retrait du rebord.
    val innerCorner = CornerRadius((TRAY_CORNER.toPx() - rail).coerceAtLeast(0f))
    val innerRect = Rect(
        offset = Offset(rail, rail),
        size = Size(size.width - rail * 2f, size.height - rail * 2f)
    )
    val cx = innerRect.center.x
    val cy = innerRect.center.y
    val hw = innerRect.width / 2f
    val hh = innerRect.height / 2f

    val innerPath = Path().apply {
        addRoundRect(RoundRect(innerRect, innerCorner))
    }
    clipPath(innerPath) {
        // Fond radial centre clair → bord sombre (légèrement translucide).
        val feltAlpha = 0.85f
        drawRect(
            brush = Brush.radialGradient(
                colors = listOf(
                    lerp(color.center, Color.White, 0.10f).copy(alpha = feltAlpha),
                    color.edge.copy(alpha = feltAlpha)
                ),
                center = Offset(cx, cy),
                radius = maxOf(hw, hh) * 1.2f
            ),
            size = size
        )
        // Taches marbrées déterministes (plus claires / plus sombres, faible alpha).
        val light = lerp(color.center, Color.White, 0.28f)
        val dark = lerp(color.edge, Color.Black, 0.25f)
        val blobRadius = minOf(hw, hh) * 0.7f
        marbleBlobs(cx, cy, hw, hh).forEach { (offset, lighter) ->
            val tint = if (lighter) light else dark
            drawCircle(
                brush = Brush.radialGradient(
                    colors = listOf(tint.copy(alpha = 0.45f), Color.Transparent),
                    center = offset,
                    radius = blobRadius
                ),
                radius = blobRadius,
                center = offset
            )
        }
        // Ombre interne (occlusion projetée par le rebord) en haut et en bas.
        drawRect(
            brush = Brush.verticalGradient(
                colors = listOf(
                    Color.Black.copy(alpha = 0.35f),
                    Color.Transparent,
                    Color.Transparent,
                    Color.Black.copy(alpha = 0.20f)
                ),
                startY = innerRect.top,
                endY = innerRect.bottom
            ),
            size = size
        )
    }
}

/** Positions déterministes des taches marbrées et si elles sont claires (true) ou sombres. */
private fun marbleBlobs(cx: Float, cy: Float, hw: Float, hh: Float): List<Pair<Offset, Boolean>> = listOf(
    Offset(cx - hw * 0.45f, cy - hh * 0.35f) to true,
    Offset(cx + hw * 0.40f, cy + hh * 0.20f) to false,
    Offset(cx + hw * 0.10f, cy - hh * 0.45f) to false,
    Offset(cx - hw * 0.20f, cy + hh * 0.40f) to true,
    Offset(cx + hw * 0.50f, cy - hh * 0.30f) to true
)
